import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface MonthValue {
  year: number
  month: number // 1..12
}

interface Props {
  value: MonthValue
  onChange: (v: MonthValue) => void
  /** Limita la navegación hacia atrás (YYYY-MM-DD). */
  minDate?: string | null
  /** Limita hacia adelante. */
  maxDate?: string | null
}

const MES_LARGO = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

function addMonths({ year, month }: MonthValue, delta: number): MonthValue {
  const idx0 = (year * 12 + (month - 1)) + delta
  return { year: Math.floor(idx0 / 12), month: (idx0 % 12) + 1 }
}

function toIso({ year, month }: MonthValue, day = 1): string {
  const yyyy = String(year)
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function parseIsoMonth(iso: string): MonthValue | null {
  const m = /^(\d{4})-(\d{2})/.exec(iso)
  if (!m) return null
  return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) }
}

function compare(a: MonthValue, b: MonthValue): number {
  return (a.year - b.year) * 12 + (a.month - b.month)
}

export function MonthFilter({ value, onChange, minDate, maxDate }: Props) {
  const minMonth = minDate ? parseIsoMonth(minDate) : null
  const maxMonth = maxDate ? parseIsoMonth(maxDate) : null

  const canPrev = !minMonth || compare(value, minMonth) > 0
  const canNext = !maxMonth || compare(value, maxMonth) < 0

  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-700 p-0.5 bg-background">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => onChange(addMonths(value, -1))}
        disabled={!canPrev}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="inline-flex items-center gap-1.5 px-2 text-sm font-medium min-w-[140px] justify-center">
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
        {MES_LARGO[value.month - 1]} {value.year}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => onChange(addMonths(value, 1))}
        disabled={!canNext}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

/** Devuelve el primer y último día del mes en formato YYYY-MM-DD. */
export function monthToRange({ year, month }: MonthValue): { desde: string; hasta: string } {
  const desde = toIso({ year, month }, 1)
  const lastDay = new Date(year, month, 0).getDate()
  const hasta = toIso({ year, month }, lastDay)
  return { desde, hasta }
}

/** Mes actual a partir de la fecha del sistema. */
export function currentMonth(): MonthValue {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}
